import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { BackofficeKnowledgeSection } from "modules/backoffice/components/knowledge/BackofficeKnowledgeSection"
import { BackofficeKnowledgeValue } from "modules/backoffice/components/knowledge/BackofficeKnowledgeValue"
import { getExperimentBadgeClassName } from "modules/backoffice/lib/experiments/getExperimentBadgeClassName"
import { formatKnowledgeRate } from "modules/backoffice/lib/knowledge/formatKnowledgeRate"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  BackofficeConversationExperimentKnowledgeAnalyticsResponseDataSchema,
  ConversationExperimentProductionName,
} from "schema"
import { z } from "zod"

type Analytics = z.infer<
  typeof BackofficeConversationExperimentKnowledgeAnalyticsResponseDataSchema
>

type Props = {
  data: Analytics | undefined
  unfilteredData: Analytics | undefined
  isPending: boolean
}

const COLUMN_COUNT = 10

export function BackofficeConversationExperimentKnowledgeTable({
  data,
  unfilteredData,
  isPending,
}: Props) {
  const { t } = useTranslation()

  const rows = useMemo(() => {
    return [...(data ?? [])].sort((left, right) => {
      if (left.experiment === ConversationExperimentProductionName) return -1
      if (right.experiment === ConversationExperimentProductionName) return 1
      return right.experiment.localeCompare(left.experiment)
    })
  }, [data])

  const unfilteredByExperiment = useMemo(
    () => unfilteredData && new Map(unfilteredData.map((row) => [row.experiment, row])),
    [unfilteredData],
  )

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={Array.from({ length: COLUMN_COUNT }, () => 1)} />
    }

    if (rows.length === 0) {
      return (
        <TableEmptySlate
          colSpan={COLUMN_COUNT}
          message={t("backoffice.conversationExperiments.chart.empty")}
        />
      )
    }

    return rows.map((row) => {
      const unfiltered = unfilteredByExperiment?.get(row.experiment)

      return (
        <TableRow key={row.experiment}>
          <TableCell>
            <div
              className={cn(
                "inline-flex w-fit max-w-full items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold shadow-xs",
                "font-mono normal-case",
                getExperimentBadgeClassName(row.experiment),
              )}
            >
              <span className="block max-w-[min(100%,16rem)] truncate">{row.experiment}</span>
            </div>
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={row.questionCount}
              unfilteredValue={unfiltered?.questionCount}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.answeredRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.answeredRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.knowledgeGapRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.knowledgeGapRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.interimCoverageRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.interimCoverageRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.knowledgeCoverageRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.knowledgeCoverageRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.knowledgeBaseCoverageRate)}
              unfilteredValue={
                unfiltered && formatKnowledgeRate(unfiltered.knowledgeBaseCoverageRate)
              }
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.knowledgeUsedRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.knowledgeUsedRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.goesInCirclesRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.goesInCirclesRate)}
            />
          </TableCell>
          <TableCell align="right">
            <BackofficeKnowledgeValue
              value={formatKnowledgeRate(row.inventedFactsRate)}
              unfilteredValue={unfiltered && formatKnowledgeRate(unfiltered.inventedFactsRate)}
            />
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <BackofficeKnowledgeSection
      title={t("backoffice.conversationExperiments.knowledgeTable.title")}
      description={t("backoffice.conversationExperiments.knowledgeTable.description")}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("backoffice.conversationExperiments.table.experiment")}</TableHead>
            <TableHead align="right">
              <Tooltip>
                <TooltipTrigger slick>
                  {t("backoffice.conversationExperiments.knowledgeTable.questionCount")}
                </TooltipTrigger>
                <TooltipContent className="max-w-90">
                  {t("backoffice.knowledge.metrics.questionCountHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right">
              <Tooltip>
                <TooltipTrigger slick>
                  {t("backoffice.conversationExperiments.knowledgeTable.answeredRate")}
                </TooltipTrigger>
                <TooltipContent className="max-w-90">
                  {t("backoffice.knowledge.metrics.answeredRateHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right">
              {t("backoffice.conversationExperiments.knowledgeTable.knowledgeGapRate")}
            </TableHead>
            <TableHead align="right">
              <Tooltip>
                <TooltipTrigger slick>
                  {t("backoffice.conversationExperiments.knowledgeTable.interimCoverageRate")}
                </TooltipTrigger>
                <TooltipContent className="max-w-90">
                  {t("backoffice.knowledge.metrics.interimCoverageRateHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right">
              <Tooltip>
                <TooltipTrigger slick>
                  {t("backoffice.conversationExperiments.knowledgeTable.knowledgeCoverageRate")}
                </TooltipTrigger>
                <TooltipContent className="max-w-90">
                  {t("backoffice.knowledge.metrics.knowledgeCoverageRateHelp")}
                </TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead align="right">
              {t("backoffice.conversationExperiments.knowledgeTable.knowledgeBaseCoverageRate")}
            </TableHead>
            <TableHead align="right">
              {t("backoffice.conversationExperiments.knowledgeTable.knowledgeUsedRate")}
            </TableHead>
            <TableHead align="right">
              {t("backoffice.conversationExperiments.knowledgeTable.goesInCirclesRate")}
            </TableHead>
            <TableHead align="right">
              {t("backoffice.conversationExperiments.knowledgeTable.inventedFactsRate")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </BackofficeKnowledgeSection>
  )
}
